'use client';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import { useT } from '../../lib/i18n';
import { SmartLink } from '../SmartLink';
import { Icon } from '../Icon';
import { Working } from '../Working';
import { toast } from '../Toast';
import { confirmLayer } from '../Alert';

/**
 * 글의 [수정]·[삭제] — **가이드 `notice-view.html` 의 머리글 오른쪽 자리**다
 * (2026-09-01 사용자 지적으로 고침). 가이드는 `cp-page-header` 오른쪽에
 * `action-btn action-cancel btn-sm` 을 아이콘과 함께 늘어놓고, 맨 아래는 [목록으로] 하나를
 * 가운데 둔다. 전에는 하단에 `action-danger` 삭제를 오른쪽에 두어 가이드와 어긋났다.
 *
 * 삭제는 가이드에 없다(관리자 버튼으로 수정·목록만 그린다) — 기능은 있어야 하므로
 * **같은 꼴**로 나란히 둔다. 새 모양을 지어내지 않는다.
 *
 * **권한은 서버가 판정한다**(`canEdit` — 작성자·관리자). 화면이 다시 셈하면 서버와 갈린다.
 * 삭제는 되돌릴 수 없으므로 확인창을 거친다. 답변이 달린 문의처럼 서버가 막는 경우가 있는데,
 * 그때는 **서버 문구를 그대로** 보여 준다 — 화면이 까닭을 지어내지 않는다.
 */
export function PostActions({ base, postId, onDeleted, editHref, deleteUrl, canEdit = true, canDelete = true, confirmText }: {
  base: string;
  postId: number;
  /** 지운 뒤 할 일 — 안 주면 목록으로 간다(FAQ 는 그 자리에서 목록을 새로 그린다) */
  onDeleted?: () => void;
  /** 수정 화면 주소 — 기본은 게시글(`{base}/{id}/edit`). 후기는 쓰기 화면에 번호를 실어 간다 */
  editHref?: string;
  /** 삭제 API — 기본은 게시글. 후기는 `/api/reviews/{id}` 다(같은 꼴, 창구만 다르다) */
  deleteUrl?: string;
  /** [수정] 을 낼지 */
  canEdit?: boolean;
  /** [삭제] 를 낼지 — 게시판이 **사용자 삭제를 막아 두면** 본인에게도 안 낸다(서버가 판정한다) */
  canDelete?: boolean;
  /** 확인창 문구 — 기본은 게시글용 */
  confirmText?: string;
}) {
  const t = useT();
  const router = useRouter();
  const [busy, setBusy] = useState(false);

  const remove = async () => {
    if (!(await confirmLayer(confirmText ?? t('bbs.deleteAsk'), { kind: 'danger', okLabel: t('common.delete') }))) return;
    setBusy(true);
    try {
      const res = await fetch(deleteUrl ?? `/api/boards/posts/${postId}`, { method: 'DELETE' });
      if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as { message?: string };
        toast(body.message ?? t('bbs.deleteFailed'), 'error');
        return;
      }
      toast(t('bbs.deleted'), 'ok');
      if (onDeleted) onDeleted();
      else router.push(base);
      router.refresh();
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      {canEdit && (
        <SmartLink href={editHref ?? `${base}/${postId}/edit`} className="action-btn action-cancel btn-sm">
          <Icon name="pencil" /> {t('action.edit')}
        </SmartLink>
      )}
      {canDelete && (
        <button type="button" className="action-btn action-cancel btn-sm" disabled={busy} onClick={() => void remove()}>
          {busy ? <Working inline /> : <Icon name="trash-2" />} {t('common.delete')}
        </button>
      )}
    </>
  );
}
